import React, { Dispatch, FC, MouseEvent, SetStateAction } from 'react';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import Badge from '@mui/material/Badge';
import ToggleButton from '@mui/material/ToggleButton';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

interface Props {
  kind: string;
  setKind: Dispatch<SetStateAction<'total' | 'employee' | 'applicant'>>;
  reviewCount: number;
  employeeReviewCount: number;
  applicantReviewCount: number;
}
const KununuBadge = styled(Badge)`
  margin-left: ${(props) => props.theme.spacing(2)};
  margin-right: ${(props) => props.theme.spacing(1.5)};
`;

const KununuToggleButton = styled(ToggleButton)`
  padding: ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(2)}
    ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(2)};
`;

const KununuToggleButtonGroup: FC<Props> = ({
  reviewCount,
  employeeReviewCount,
  applicantReviewCount,
  kind,
  setKind,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleChange = (
    event: MouseEvent<HTMLElement>,
    newValue: 'total' | 'employee' | 'applicant' | null
  ) => {
    if (newValue !== null) {
      setKind(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      color="primary"
      value={kind}
      exclusive
      onChange={handleChange}
      sx={{
        '& .Mui-selected.MuiToggleButtonGroup-grouped': {
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <KununuToggleButton
        disabled={employeeReviewCount === 0}
        value="employee"
        size="small"
      >
        <Typography variant="body2">{t('employees')}</Typography>
        <KununuBadge
          showZero
          badgeContent={employeeReviewCount}
          color={employeeReviewCount === 0 ? 'default' : 'primary'}
          sx={{ pl: theme.spacing(1) }}
        />
      </KununuToggleButton>
      <KununuToggleButton
        disabled={applicantReviewCount === 0}
        value="applicant"
        size="small"
      >
        <Typography variant="body2">{t('applicants')}</Typography>
        <KununuBadge
          showZero
          badgeContent={applicantReviewCount}
          color={applicantReviewCount === 0 ? 'default' : 'primary'}
          sx={{ pl: theme.spacing(1), backgroundColor: theme.palette.error.main }}
        />
      </KununuToggleButton>
      <KununuToggleButton
        disabled={reviewCount === 0}
        value="total"
        size="small"
      >
        <Typography variant="body2">{t('all')}</Typography>
        <KununuBadge
          showZero
          badgeContent={reviewCount}
          color={reviewCount === 0 ? 'default' : 'primary'}
          sx={{ pl: theme.spacing(1) }}
        />
      </KununuToggleButton>
    </ToggleButtonGroup>
  );
};

export default KununuToggleButtonGroup;
